import { useCallback, useMemo } from 'react';
import { useCollectionItem, useCollectionItems, useCollectionQuery, useLiveQuery } from '@rocketlane/state-db';
import { taskCollection } from 'collections/tasks';
import { extractParts, PhaseEntityType } from 'data/projects/phase-entity';
import { logger } from 'services';
import { fetchLaneFirstPage, fetchLaneNextPage, fetchPlanBoard, getPlanBoardFetchKey, getPlanLaneFetchKey } from './fetchers';
import { getPlanLaneKey, planBoardPhaseCollection, planLaneCollection } from './plan-lane-collection';
// Keep the collections layer free of `queries/*` imports (RQ is being removed
// per entity) — same staleness the RQ layer used.
const tenMinutes = 10 * 60 * 1000;
const EMPTY_TASKS = [];
const EMPTY_TOKENS = [];
const EMPTY_SPRINTS = [];
/** Element-wise reference equality for live-query array results. */ const sameElementRefs = (a, b)=>{
    if (a === b) {
        return true;
    }
    if (a.length !== b.length) {
        return false;
    }
    return a.every((item, i)=>Object.is(item, b[i]));
};
/**
 * One lane's pagination state (id window + cursor + counts), collection-backed
 * and reactive. Mounting an enabled lane triggers its (batch-coalesced) first
 * entities-page fetch; entities themselves land in `taskCollection` and are
 * joined at render via `useCollectionItems` — see plan-lane-collection.ts for
 * the ids-only invariant.
 */ export const usePlanLane = (projectId, phaseId, filters, options = {})=>{
    var _options_enabled;
    const laneKey = getPlanLaneKey(projectId, phaseId, filters);
    return useCollectionQuery({
        collection: planLaneCollection,
        queryKey: getPlanLaneFetchKey(laneKey),
        queryFn: ()=>fetchLaneFirstPage(projectId, phaseId, filters),
        select: (c)=>c.get(laneKey),
        enabled: ((_options_enabled = options.enabled) !== null && _options_enabled !== void 0 ? _options_enabled : true) && projectId > 0 && phaseId > 0,
        staleMs: tenMinutes
    });
};
export const useLaneEntities = (projectId, phaseId, filters, options = {})=>{
    var _ref, _ref1, _ref2;
    const laneQuery = usePlanLane(projectId, phaseId, filters, options);
    const lane = laneQuery.data;
    const pageTokens = lane === null || lane === void 0 ? void 0 : lane.pageTokens;
    const taskIds = useMemo(()=>(pageTokens !== null && pageTokens !== void 0 ? pageTokens : []).map((token)=>extractParts(token)).filter((parts)=>parts.entityType === PhaseEntityType.Task).map((parts)=>parts.id), [
        pageTokens
    ]);
    // Per-id subscriptions: an edit to any lane task repaints just this lane.
    const tasks = useCollectionItems(taskCollection, taskIds);
    // Membership discovery (a subtask created/moved under a lane parent) can't
    // be done per-id — live-select over the collection, keyed by the parents.
    // Only windowed (mounted) lanes pay this, and only in paginated mode.
    // Return the stable EMPTY constant for no-match and compare element-wise —
    // useLiveQuery's default Object.is would treat every recomputed (fresh)
    // array as a change and re-render this lane on EVERY collection write.
    const parentIdsKey = taskIds.join(',');
    const subTasks = useLiveQuery(taskCollection, (items)=>{
        if (taskIds.length === 0) {
            return EMPTY_TASKS;
        }
        const parentSet = new Set(taskIds);
        const matched = items.filter((task)=>{
            var _ref;
            var _task_parentTaskObject;
            const parentId = (_ref = (_task_parentTaskObject = task.parentTaskObject) === null || _task_parentTaskObject === void 0 ? void 0 : _task_parentTaskObject.taskId) !== null && _ref !== void 0 ? _ref : task.parentTask;
            return parentId != null && parentSet.has(Number(parentId));
        });
        return matched.length === 0 ? EMPTY_TASKS : matched;
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        parentIdsKey
    ], sameElementRefs);
    const fetchNextPage = useCallback(()=>{
        fetchLaneNextPage(projectId, phaseId, filters).catch((error)=>{
            // Load-more failure is non-fatal: the trigger stays mounted and the
            // user can scroll again. Log — never swallow silently.
            logger.error('plan-board fetchLaneNextPage failed', {
                error,
                projectId,
                phaseId
            });
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        projectId,
        phaseId,
        lane === null || lane === void 0 ? void 0 : lane.filtersHash
    ]);
    // Stable fallback identities — a fresh `[]` per render would churn every
    // downstream identity-compared dep (the DnD hook applies `entityOrder` via
    // an identity-guarded effect that calls setState; a new array each render
    // makes that an infinite render loop).
    return {
        entityOrder: pageTokens !== null && pageTokens !== void 0 ? pageTokens : EMPTY_TOKENS,
        tasks,
        subTasks,
        sprints: (_ref = lane === null || lane === void 0 ? void 0 : lane.sprints) !== null && _ref !== void 0 ? _ref : EMPTY_SPRINTS,
        totalCount: (_ref1 = lane === null || lane === void 0 ? void 0 : lane.totalCount) !== null && _ref1 !== void 0 ? _ref1 : 0,
        filteredCount: (_ref2 = lane === null || lane === void 0 ? void 0 : lane.filteredCount) !== null && _ref2 !== void 0 ? _ref2 : 0,
        hasNextPage: Boolean(lane === null || lane === void 0 ? void 0 : lane.nextCursor),
        isFetchingNextPage: Boolean(lane === null || lane === void 0 ? void 0 : lane.isLoadingMore),
        fetchNextPage,
        isError: laneQuery.isError,
        hasLoaded: lane != null
    };
};
/**
 * ONE phase's board aggregates, per-id subscription. Lanes read their board
 * phase through this (not a shared Map in context) so a one-phase patch —
 * status pill, phase edit, progress reconciler delta — repaints exactly ONE
 * lane instead of invalidating a context value every lane consumes.
 */ export const usePlanBoardPhase = (phaseId)=>useCollectionItem(planBoardPhaseCollection, phaseId > 0 ? phaseId : null);
/**
 * Board aggregates of a project, keyed by phaseId — the shape
 * plan-lane-context's `boardPhasesById` consumers expect.
 */ export const usePlanBoardPhases = (projectId, options = {})=>{
    var _options_enabled;
    const { filters } = options;
    const result = useCollectionQuery({
        collection: planBoardPhaseCollection,
        // Keyed by the filters hash: a filter change must re-fetch the board so
        // per-phase `filteredEntityCount` reflects the active filters.
        queryKey: getPlanBoardFetchKey(projectId, filters),
        queryFn: ()=>fetchPlanBoard(projectId, filters),
        select: (c)=>c.getByIndex('byProject', projectId),
        enabled: ((_options_enabled = options.enabled) !== null && _options_enabled !== void 0 ? _options_enabled : true) && projectId > 0,
        staleMs: tenMinutes
    });
    const phasesById = useMemo(()=>{
        var _result_data;
        return new Map(((_result_data = result.data) !== null && _result_data !== void 0 ? _result_data : []).map((entry)=>[
                entry.projectPhaseId,
                entry
            ]));
    }, [
        result.data
    ]);
    return {
        ...result,
        phasesById
    };
};
